// types
import { Fulfillment } from "@secondcloset/types";
import { InventoryReceivingItemsTableTypes } from "@secondcloset/web-components";
type Shipment = Fulfillment.Shipment;
type SecondClosetShippingMethod = Fulfillment.SecondClosetShippingMethod;
type ShippingMethodStatus = Fulfillment.ShippingMethodStatus;
type ShipmentItem = Fulfillment.ShipmentItem;
type InventoryReceivingItemStatus = InventoryReceivingItemsTableTypes.ItemStatus;

// enums
const InventoryReceivingItemStatus = InventoryReceivingItemsTableTypes.ItemStatus;

export const isInventoryWarehouseReceiving = (shipment: Shipment) => {
  const shippingMethod = shipment.shipping_method as SecondClosetShippingMethod;
  return shippingMethod.job_type === "inventory_warehouse_receiving";
};

const item_failed_reason = (failed_reason: string | null) => {
  switch (failed_reason?.toLocaleLowerCase()) {
    case InventoryReceivingItemStatus.missingItem.toLocaleLowerCase():
      return InventoryReceivingItemStatus.missingItem;
    case InventoryReceivingItemStatus.damaged.toLocaleLowerCase():
      return InventoryReceivingItemStatus.damaged;
    case InventoryReceivingItemStatus.wrongItem.toLocaleLowerCase():
      return InventoryReceivingItemStatus.wrongItem;
    case InventoryReceivingItemStatus.other.toLocaleLowerCase():
    default:
      return InventoryReceivingItemStatus.other;
  }
};

export const getItemStatus = (item: ShipmentItem) => {
  const actionStatus = item?.shipment_actions_status?.inventory_warehouse_receiving;
  if (!actionStatus) return null;
  if (actionStatus.completed) return InventoryReceivingItemStatus.received;
  if (!actionStatus.failed) return InventoryReceivingItemStatus.toBeProcessed; // both complete and fail is false
  return item_failed_reason(actionStatus.failed_reason);
};

export const getItemStatusV2 = (item: Fulfillment.ShipmentItem<"V2">, itemID: string) => {
  const actionStatus = item?.shipment_actions_status?.inventory_warehouse_receiving;
  if (!actionStatus) return null;
  if (actionStatus.completed_shipment_item_ids.includes(itemID)) return InventoryReceivingItemStatus.received;
  const failed_status = actionStatus.failed_shipment_items.filter((failed_type) => {
    return failed_type.ids.includes(itemID);
  });
  if (failed_status.length === 0) return InventoryReceivingItemStatus.toBeProcessed; // both complete and fail is false
  return item_failed_reason(failed_status[0].failed_reason);
};

export const hasAnyIdleItem = (shipment: Shipment) => {
  if (!isInventoryWarehouseReceiving(shipment)) return undefined;
  return shipment?.shipment_items?.some((item) => getItemStatus(item) === InventoryReceivingItemStatus.toBeProcessed);
};

export const constructUpdateItemStatusBody = (itemIDs: string[], newStatus: InventoryReceivingItemStatus) => {
  const isFailedStatus = (status: InventoryReceivingItemStatus) => {
    const failedStatus = [
      InventoryReceivingItemStatus.damaged,
      InventoryReceivingItemStatus.missingItem,
      InventoryReceivingItemStatus.wrongItem,
      InventoryReceivingItemStatus.other,
    ];
    return failedStatus.some((fail) => fail === status);
  };

  const action = {
    action: "inventory_warehouse_receiving",
    completed: newStatus === InventoryReceivingItemStatus.received,
    failed: isFailedStatus(newStatus),
    failed_reason: isFailedStatus(newStatus) ? newStatus.toString() : null,
  };

  const shipmentItems = itemIDs.reduce((acc, itemID) => {
    acc.push({
      id: itemID,
      actions: [action],
    });
    return acc;
  }, [] as any[]);

  return { shipment_items: shipmentItems };
};

type ItemAction = {
  action?: string;
  completed?: boolean;
  failed?: boolean;
  failed_reason?: string;
};
export const customerPickupItemAction = (shipmentItems: Fulfillment.ShipmentItem[], itemAction: ItemAction) => {
  return {
    shipment_items: shipmentItems.reduce((acc, cur) => {
      acc.push({ id: cur.id, actions: [itemAction] });
      return acc;
    }, [] as { id: string; actions: ItemAction[] }[]),
  };
};

export const customerPickupItemActionV2 = (shipmentItems: Fulfillment.ShipmentItem<"V2">[], itemAction: ItemAction) => {
  return {
    shipment_items: shipmentItems
      .flatMap((si) => si.shipment_item_ids)
      .reduce((acc, id) => {
        acc.push({ id, actions: [itemAction] });
        return acc;
      }, [] as { id: string; actions: ItemAction[] }[]),
  };
};
